import { HASH_PARAMS, resolveApiBase, type BetlensLookup } from '@sdk/contract';

/**
 * Bootstrap context comes from the URL **hash**, never the query string.
 *
 * The fragment is not sent to the server hosting this bundle (Cloudflare), so
 * the JWT never reaches their logs — while `location.hash` stays fully readable
 * to us. This is the whole reason the parent panel embeds us as:
 *
 *   <iframe src="https://betlens.pages.dev/#t=<jwt>&api=https://panelA-backend/betlens">
 *
 * No postMessage: bulk data never crosses the frame boundary. We take a few
 * hundred bytes of context and pull everything else ourselves.
 *
 * ⚠️ The param NAMES come from `sdk/src/contract.ts` — the same file the web SDK
 * writes them with. Both halves of the bootstrap agreement are single-sourced on
 * purpose: rename a param and this stops compiling, instead of shipping a
 * console that silently boots with no API base.
 */

export interface Bootstrap {
  /** Short-lived JWT minted by the host panel. */
  token: string | null;
  /** Absolute base URL of the panel's mounted betlens API, once resolved. */
  api: string | null;
  theme: 'dark' | 'light' | null;
  /** Optional deep-link: pre-fill and run a lookup on load. */
  lookup: LookupParams | null;
  /**
   * Set when an `api` WAS supplied but could not be resolved — so the bootstrap
   * form can say why instead of looking like nothing was passed at all.
   */
  apiError: string | null;
}

/**
 * The origin of the page that embedded us, or null when we are not framed.
 *
 * Two mechanisms because no single one is portable:
 *  - `ancestorOrigins` is exact but Chromium/WebKit only (no Firefox);
 *  - `document.referrer` covers Firefox — for a framed document it is the
 *    embedding page's URL — but is empty under a strict Referrer-Policy.
 *
 * Index 0 is the IMMEDIATE parent, which is the panel that embedded us; the
 * last entry is the top-level document, which may be something else entirely if
 * the panel is itself framed.
 */
export function embedderOrigin(): string | null {
  if (window.self === window.top) return null;

  const ancestors = window.location.ancestorOrigins;
  if (ancestors && ancestors.length > 0) {
    const origin = ancestors[0];
    // A sandboxed or data: parent reports "null" — a string, not a value we can use.
    if (origin && origin !== 'null') return origin;
  }

  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      /* malformed referrer — fall through */
    }
  }
  return null;
}

/** The console's lookup shape IS the SDK's — one contract, one type. */
export type LookupParams = BetlensLookup;

function parseHash(): URLSearchParams {
  return new URLSearchParams(window.location.hash.replace(/^#/, ''));
}

/**
 * Read the bootstrap context from the URL hash ONLY.
 *
 * The API base and token are never persisted — not localStorage, not cookies.
 * They live in the fragment (for an embedded panel) or in React state after the
 * dev form (for a direct open), and vanish when the tab closes. The token is a
 * short-lived JWT, so keeping it out of any store is deliberate: nothing to leak
 * from disk, nothing stale to reuse next session.
 */
export function readBootstrap(): Bootstrap {
  const h = parseHash();

  const theme = h.get(HASH_PARAMS.theme);
  const play_service_id = h.get(HASH_PARAMS.playServiceId) ?? h.get('play_service_id');
  const game_uuid = h.get(HASH_PARAMS.gameUuid) ?? undefined;
  const bet_id = h.get(HASH_PARAMS.betId) ?? undefined;
  const transaction_id = h.get(HASH_PARAMS.transactionId) ?? undefined;
  const cycle_uniq_id = h.get(HASH_PARAMS.cycleUniqId) ?? undefined;
  const tr_uniq_id = h.get(HASH_PARAMS.trUniqId) ?? undefined;
  const round_id = h.get(HASH_PARAMS.roundId) ?? undefined;

  const lookup: LookupParams | null =
    play_service_id && (bet_id || transaction_id || cycle_uniq_id || tr_uniq_id || round_id)
      ? { play_service_id, game_uuid, bet_id, transaction_id, cycle_uniq_id, tr_uniq_id, round_id }
      : null;

  // The panel may pass `api` relative (/api/v1/betlens) precisely so it does not
  // have to know its own public hostname — one bundle, many environments. We
  // resolve it against the origin that embedded us, because that IS the panel.
  const rawApi = h.get(HASH_PARAMS.api);
  const parent = embedderOrigin();
  const api = resolveApiBase(rawApi, parent);

  return {
    token: h.get(HASH_PARAMS.token) ?? null,
    api,
    theme: theme === 'dark' || theme === 'light' ? theme : null,
    lookup,
    apiError: rawApi && !api ? explainApi(rawApi, parent) : null,
  };
}

/** Why a supplied `api` did not resolve — shown in the bootstrap form. */
function explainApi(rawApi: string, parent: string | null): string {
  if (!rawApi.startsWith('/')) {
    return `The embedding page passed a relative API path ("${rawApi}") without a leading "/". It must be absolute, or root-relative like "/api/v1/betlens".`;
  }
  if (!parent) {
    return `The API path "${rawApi}" is relative to the page that embeds this console, but this console was opened directly — there is no embedding page to resolve it against. Enter the full URL below.`;
  }
  return `The API base "${rawApi}" could not be resolved against ${parent}.`;
}

/**
 * Push the current lookup into the hash so a support agent can copy the URL and
 * hand a colleague the exact same bet. Deliberately preserves `t`/`api`/`theme`.
 */
export function writeLookupToHash(lookup: LookupParams): void {
  const h = parseHash();
  h.set(HASH_PARAMS.playServiceId, lookup.play_service_id);

  const pairs = [
    [HASH_PARAMS.gameUuid, lookup.game_uuid],
    [HASH_PARAMS.betId, lookup.bet_id],
    [HASH_PARAMS.transactionId, lookup.transaction_id],
    [HASH_PARAMS.cycleUniqId, lookup.cycle_uniq_id],
    [HASH_PARAMS.trUniqId, lookup.tr_uniq_id],
    [HASH_PARAMS.roundId, lookup.round_id],
  ] as const;

  for (const [param, value] of pairs) {
    if (value) h.set(param, value);
    else h.delete(param);
  }

  history.replaceState(null, '', `#${h.toString()}`);
}

export function setThemeInHash(theme: 'dark' | 'light'): void {
  const h = parseHash();
  h.set(HASH_PARAMS.theme, theme);
  history.replaceState(null, '', `#${h.toString()}`);
}

/**
 * The active functional area lives in the hash too (`view=`), so it composes
 * with the bootstrap params instead of a router fighting them — and a view is
 * shareable/deep-linkable like everything else.
 */
export function readViewFromHash(): string | null {
  return parseHash().get('view');
}

export function setViewInHash(view: string): void {
  const h = parseHash();
  h.set('view', view);
  history.replaceState(null, '', `#${h.toString()}`);
}
